import { $UserAgent } from '@lemon8/web-app-shared/atom';
import { FC, useEffect } from 'react';
import {
  $LabelVisible,
  $LabelSwitcherVisible,
  $CurrentGallerySlide,
  $CurrentGalleryArticle,
  $CurrentMainSlide,
  $EnableIndexForRecommend,
  $CurrentEndRecommendSlide,
  $EndRecommendSwiper,
  $GallerySwiper,
  $MainSwiper,
  $CurrentArticleRelatedPost,
  useMountCurrentSlideWithSwiper,
  $NestedSwiperSlideInfo,
  $SlideToNestedIndex,
  EArticleGalleryLayoutStrategy,
  $LayoutStrategy,
  $RootPaddingBottom,
  $LoadArticleRelatedPosts,
  $CurrentArticleLoadableRelatedPost,
} from './atoms';
import {
  DotIndicatorSection,
  EndRecommendSection,
  GalleryIndex,
  GallerySection,
  LabelVisibleSwitcher,
  MainSwiper,
  SwiperAction,
  WarningLabelSection,
} from './components';
import { IGalleryArticleDetail } from 'shared/models';
import { PromiseOrValue } from 'shared/utils/promise-or-value';
import { IArticleRelatedPost } from 'shared/atoms';
import { ClickPosition, ClickPositionPrefix } from '~/utils/click-position';
import { sendNotification } from '~/utils/pwa/notification';
import { useAtomValue } from 'jotai';
import { generateOneLink } from '~/utils/active';
import { ScopedProvider } from '@lemon8/web-app-shared/ssr';
import { $CurrentPositionPrefix } from '~/models/current-position-prefix';
import { $AdditionalParams } from '~/models/additional-params';
import { AdditionalParamsOptionalProps, PositionPrefixOptionalProps } from '../immersive-post-item/shared/typing';

const ArticleGalleryImpl: FC = () => {
  useMountCurrentSlideWithSwiper($MainSwiper, $CurrentMainSlide);
  useMountCurrentSlideWithSwiper($GallerySwiper, $CurrentGallerySlide);

  return (
    <>
      <SwiperAction>
        <MainSwiper>
          <GallerySection>
            <LabelVisibleSwitcher />
            <GalleryIndex />
            <WarningLabelSection />
          </GallerySection>
          <EndRecommendSection />
        </MainSwiper>
      </SwiperAction>
      <DotIndicatorSection />
    </>
  );
};

interface IArticleGalleryProps {
  /** data */
  article: IGalleryArticleDetail;
  articleRelatedPosts?: PromiseOrValue<IArticleRelatedPost>;
  loadArticleRelated?: () => void;

  /** config */
  enableIndexForRecommend: boolean;
  layoutStrategy?: EArticleGalleryLayoutStrategy;
}

export const ArticleGallery: FC<PositionPrefixOptionalProps & AdditionalParamsOptionalProps & IArticleGalleryProps> = (
  props,
) => {
  const {
    article,
    enableIndexForRecommend,
    articleRelatedPosts,
    layoutStrategy = EArticleGalleryLayoutStrategy.SQUARE,
    loadArticleRelated,
    positionPrefix = ClickPositionPrefix.none,
    additionalParams = {},
  } = props;

  const { title, shortContent, imageList, groupId } = article;
  const userAgent = useAtomValue($UserAgent);
  const { isBot } = userAgent;

  useEffect(() => {
    /** 含有is_notification'说明本次发送过了 */
    if (!sessionStorage.getItem('is_notification') && !isBot) {
      const imgUrl = imageList && imageList[0].url;
      const oneLink = generateOneLink({
        pid: 'website_seo_notification',
        schemaOption: { pageType: 'article', groupId },
        ampExtra: {
          enter_position: ClickPosition.pwa_notification_banner,
          enter_page_id: groupId,
          enter_page_type: 'article',
        },
      });
      sendNotification(title, {
        body: shortContent,
        icon: 'https://p16-va.lemon8cdn.com/tos-maliva-p-0000/5d8e5f313d1b470799f8450327ec6dee~tplv-tej9nj120t-scaling:120:0:q75.jpeg',
        requireInteraction: true,
        image: imgUrl,
        data: {
          time: new Date(Date.now()).toString(),
          url: oneLink,
        },
      });
    }
  }, []);

  return (
    <ScopedProvider
      atoms={[
        /** swiper */
        $MainSwiper,
        $GallerySwiper,
        $EndRecommendSwiper,

        /** swiper slide */
        $CurrentMainSlide,
        $CurrentGallerySlide,
        $CurrentEndRecommendSlide,
        $CurrentArticleLoadableRelatedPost,

        /** nested swiper slides */
        $NestedSwiperSlideInfo,
        $SlideToNestedIndex,

        // gallery label
        $LabelVisible,
        $LabelSwitcherVisible,

        /** additional config */
        $RootPaddingBottom,
      ]}
      atomsWithHydration={[
        [$UserAgent, userAgent],
        [$CurrentGalleryArticle, article],
        [$CurrentArticleRelatedPost, articleRelatedPosts],
        [$EnableIndexForRecommend, enableIndexForRecommend],
        [$LayoutStrategy, layoutStrategy],
        [$LoadArticleRelatedPosts, { loader: loadArticleRelated }],
        [$CurrentPositionPrefix, positionPrefix],
        [$AdditionalParams, additionalParams],
      ]}
    >
      <ArticleGalleryImpl />
    </ScopedProvider>
  );
};
